import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { supplierAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Package, CheckCircle2, Clock, AlertTriangle, TrendingUp, ShoppingCart, Euro, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend, Area, AreaChart
} from 'recharts';

const COLORS = ['#059669', '#0891b2', '#7c3aed', '#e11d48', '#ea580c', '#ca8a04'];
const CATEGORY_LABELS = {
    'cat-fruits': 'Fruits',
    'cat-legumes': 'Legumes',
    'cat-bio': 'Bio',
    'cat-plantes': 'Plantes & Fleurs',
    'cat-alimentaire-africain': 'Alimentaire Africain',
    'cat-cosmetique-africain': 'Cosmetique Africain',
};

const formatEuro = (val) => `${Number(val).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} EUR`;
const formatMonth = (m) => {
    if (!m) return '';
    const [y, mo] = m.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(mo) - 1]} ${y.slice(2)}`;
};

export default function SupplierDashboard() {
    const { user } = useAuth();
    const [products, setProducts] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadData = useCallback(async () => {
        try {
            const [prodRes, analyRes] = await Promise.all([
                supplierAPI.getProducts(),
                supplierAPI.getAnalytics(),
            ]);
            setProducts(prodRes.data.products || []);
            setAnalytics(analyRes.data);
        } catch (err) {
            toast.error('Erreur chargement des donnees');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const pendingCount = products.filter(p => p.supplier_update_pending).length;
    const activeCount = products.filter(p => p.ready_to_order).length;
    const totalCount = products.length;

    // useMemo must be called before any early returns to follow Rules of Hooks
    const recentModifiedProducts = useMemo(() =>
        products.filter(p => p.supplier_updated_at)
            .sort((a, b) => (b.supplier_updated_at || '').localeCompare(a.supplier_updated_at || ''))
            .slice(0, 5),
        [products]
    );
    const hasRecentModifications = recentModifiedProducts.length > 0;

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-gray-400">Chargement du tableau de bord...</div></div>;

    const kpis = analytics?.kpis || {};
    const monthly = analytics?.monthly_evolution || [];
    const topProducts = analytics?.top_products || [];
    const categories = (analytics?.categories || []).map(c => ({
        ...c,
        label: CATEGORY_LABELS[c.category] || c.category
    }));
    const topClients = analytics?.top_clients || [];
    const stockAlerts = analytics?.stock_alerts || [];

    return (
        <div className="space-y-6" data-testid="supplier-dashboard">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Bienvenue, {user?.contact_name || user?.company_name}</h1>
                <p className="text-sm text-gray-500 mt-1">Code fournisseur : <span className="font-mono font-bold text-emerald-700">{user?.code}</span></p>
            </div>

            {/* Product Status Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: 'Total produits', value: totalCount, icon: Package, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'En ligne', value: activeCount, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'En attente', value: pendingCount, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
                    { label: 'Hors ligne', value: totalCount - activeCount, icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
                ].map(s => (
                    <Card key={s.label} className="rounded-xl" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                                <s.icon className={`w-5 h-5 ${s.color}`} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{s.value}</p>
                                <p className="text-xs text-gray-500">{s.label}</p>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* KPI Revenue Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: 'Chiffre d\'affaires', value: formatEuro(kpis.total_revenue || 0), icon: Euro, color: 'text-emerald-700', bg: 'bg-emerald-50' },
                    { label: 'Commandes', value: kpis.total_orders || 0, icon: ShoppingCart, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                    { label: 'Panier moyen', value: formatEuro(kpis.avg_order_value || 0), icon: TrendingUp, color: 'text-cyan-600', bg: 'bg-cyan-50' },
                    { label: 'Quantite vendue', value: `${kpis.total_quantity || 0} u.`, icon: BarChart3, color: 'text-violet-600', bg: 'bg-violet-50' },
                ].map(s => (
                    <Card key={s.label} className="rounded-xl border-0 shadow-sm" data-testid={`kpi-${s.label.toLowerCase().replace(/['\s]/g, '-')}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center`}>
                                <s.icon className={`w-5 h-5 ${s.color}`} />
                            </div>
                            <div>
                                <p className="text-xl font-bold">{s.value}</p>
                                <p className="text-xs text-gray-500">{s.label}</p>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue Evolution */}
                <Card className="rounded-xl" data-testid="chart-revenue-evolution">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Evolution du CA</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {monthly.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={monthly}>
                                    <defs>
                                        <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                                            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} EUR`} />
                                    <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} EUR`, 'CA']} labelFormatter={formatMonth} />
                                    <Area type="monotone" dataKey="revenue" stroke="#059669" fill="url(#gradRevenue)" strokeWidth={2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="text-sm text-gray-400 text-center py-12">Aucune donnee disponible</p>
                        )}
                    </CardContent>
                </Card>

                {/* Orders per month */}
                <Card className="rounded-xl" data-testid="chart-orders-evolution">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Commandes par mois</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {monthly.length > 0 ? (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={monthly}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip formatter={(v, name) => [v, name === 'orders' ? 'Commandes' : 'Quantite']} labelFormatter={formatMonth} />
                                    <Bar dataKey="orders" fill="#6366f1" radius={[4, 4, 0, 0]} name="Commandes" />
                                    <Bar dataKey="quantity" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Quantite" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="text-sm text-gray-400 text-center py-12">Aucune donnee disponible</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top Products */}
                <Card className="rounded-xl lg:col-span-2" data-testid="chart-top-products">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Top produits (CA)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {topProducts.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={topProducts} layout="vertical" margin={{ left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} EUR`} />
                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                                    <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} EUR`, 'CA']} />
                                    <Bar dataKey="revenue" fill="#059669" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="text-sm text-gray-400 text-center py-12">Aucune vente enregistree</p>
                        )}
                    </CardContent>
                </Card>

                {/* Category Distribution */}
                <Card className="rounded-xl" data-testid="chart-categories">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Repartition categories</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {categories.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={categories} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={3}>
                                        {categories.map((cat) => <Cell key={cat.label} fill={COLORS[categories.indexOf(cat) % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v, name) => [v, name]} />
                                    <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="text-sm text-gray-400 text-center py-12">Aucune categorie</p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Third Row: Clients + Stock + Recent */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top Clients */}
                <Card className="rounded-xl" data-testid="top-clients">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Meilleurs clients</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {topClients.length > 0 ? topClients.map((c, i) => (
                                <div key={c.name} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                                        <span className="text-sm font-medium">{c.name}</span>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-bold">{formatEuro(c.revenue)}</p>
                                        <p className="text-xs text-gray-400">{c.orders} cmd</p>
                                    </div>
                                </div>
                            )) : (
                                <p className="text-sm text-gray-400 text-center py-4">Aucun client</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Stock Alerts */}
                <Card className="rounded-xl" data-testid="stock-alerts">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            Alertes stock
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {stockAlerts.length > 0 ? stockAlerts.map(s => (
                                <div key={s.product_code} className="flex items-center justify-between py-1.5 border-b last:border-0">
                                    <div>
                                        <p className="text-sm font-medium">{s.name}</p>
                                        <p className="text-xs text-gray-400">{s.product_code}</p>
                                    </div>
                                    <span className={`text-sm font-bold ${s.stock < 5 ? 'text-red-600' : 'text-amber-600'}`}>
                                        {s.stock} u.
                                    </span>
                                </div>
                            )) : (
                                <p className="text-sm text-emerald-600 text-center py-4">Tous les stocks sont OK</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-xl" data-testid="recent-modifications">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Derniers produits modifies</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {recentModifiedProducts.map(p => (
                                <div key={p.id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                                    <div>
                                        <p className="text-sm font-medium">{p.name}</p>
                                        <p className="text-xs text-gray-400">{p.product_code}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-bold">{p.price ? `${p.price.toFixed(2)} EUR` : 'N/A'}</p>
                                        {p.supplier_update_pending && <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">En attente</span>}
                                    </div>
                                </div>
                            ))}
                            {!hasRecentModifications && (
                                <p className="text-sm text-gray-400 text-center py-4">Aucune modification recente</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
